import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { usePreferenceStore, usePreferenceActions } from '@/app/store/preferenceStore';
import { useAuthStore } from '@/app/store/authStore';
import { useRole } from '@/app/hooks/useRole';
import { listSites } from '@/app/services/siteService';
import { listGodowns } from '@/app/services/inventoryService';
import { updateUserPreferences } from '@/app/services/userService';
import { Site } from '@/app/types/site';
import { Godown } from '@/app/types/inventory';
import { UserPreferenceUpdate } from '@/app/types/user_preference';

interface ProfilePopupProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Define form schema
const preferenceSchema = z.object({
  defaultSiteId: z.number().nullable().optional(),
  defaultGodownId: z.number().nullable().optional(),
});

type PreferenceFormData = z.infer<typeof preferenceSchema>;

export const ProfilePopup: React.FC<ProfilePopupProps> = ({ isOpen, onOpenChange }) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { isClientManager } = useRole();
  const { defaultSiteId, defaultGodownId, isPreferencesLoaded } = usePreferenceStore();
  const { setDefaultSiteId, setDefaultGodownId } = usePreferenceActions();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<PreferenceFormData>({
    resolver: zodResolver(preferenceSchema),
    defaultValues: {
      defaultSiteId: defaultSiteId,
      defaultGodownId: defaultGodownId,
    },
  });

  // Reset form when preferences load or change in the store, or when dialog opens
  useEffect(() => {
    if (isOpen && isPreferencesLoaded) {
      reset({
        defaultSiteId: defaultSiteId,
        defaultGodownId: defaultGodownId,
      });
    }
  }, [isOpen, defaultSiteId, defaultGodownId, reset, isPreferencesLoaded]);

  // Fetch Sites - Updated to match sales-invoices pattern
  const {
    data: sitesData = { items: [] },
    isLoading: isLoadingSites,
    isError: isErrorSites,
    error: errorSites
  } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 }),
    enabled: isOpen,
  });

  // Fetch Godowns - Only for users with godown access (not client_manager)
  const {
    data: godownsData = { items: [] },
    isLoading: isLoadingGodowns,
    isError: isErrorGodowns,
    error: errorGodowns
  } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 }),
    enabled: isOpen && !isClientManager, // Don't fetch godowns for client_manager
  });

  // Mutation for updating preferences
  const updateMutation = useMutation({
    mutationFn: updateUserPreferences,
    onSuccess: (data) => {
      toast.success('Preferences updated successfully!');
      // Update Zustand store with the saved values
      setDefaultSiteId(data.default_site_id);
      setDefaultGodownId(data.default_godown_id);
      // Manually reset form to match new store state and clear dirty flag
      reset({
        defaultSiteId: data.default_site_id,
        defaultGodownId: data.default_godown_id,
      });
      // Invalidate queries if other parts of the app depend on this
      queryClient.invalidateQueries({ queryKey: ['userPreferences'] });
      onOpenChange(false); // Close dialog on success
      
      // Perform full page refresh to sync token states
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update preferences: ${error.message}`);
      console.error('Preference update failed:', error);
    },
  });

  const onSubmit = (data: PreferenceFormData): void => {
    console.log('Submitting preferences:', data);
    const payload: UserPreferenceUpdate = {
      default_site_id: data.defaultSiteId,
      default_godown_id: data.defaultGodownId,
    };
    updateMutation.mutate(payload);
  };

  const isLoading = isLoadingSites || isLoadingGodowns;
  const hasError = isErrorSites || isErrorGodowns;
  const error = errorSites || errorGodowns;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Profile & Preferences</DialogTitle>
          <DialogDescription>
            View your details and set application defaults.
          </DialogDescription>
        </DialogHeader>

        {/* Display User Info */}
        <div className="p-4 mb-4 border rounded-md bg-muted/50 text-sm">
          <p><strong>Username:</strong> {user?.username}</p>
          <p><strong>Email:</strong> {user?.email}</p>
          <p><strong>Name:</strong> {user?.full_name || 'N/A'}</p>
          <p><strong>Role:</strong> {user?.role?.name || 'N/A'}</p>
        </div>

        {/* Preferences Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <h3 className="text-md font-semibold">Defaults</h3>

          {hasError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Error loading Sites or Godowns: {(error as Error)?.message}
              </AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2">Loading defaults...</span>
            </div>
          ) : (
            <>
              {/* Site Selection */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="defaultSiteId" className="text-right col-span-1">
                  Site
                </Label>
                <Controller
                  name="defaultSiteId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value?.toString() ?? undefined}
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                      disabled={updateMutation.isPending}
                    >
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Select default site" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">None</SelectItem>
                        {sitesData.items?.map((site: Site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {/* Godown Selection */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="defaultGodownId" className="text-right col-span-1">
                  Warehouse
                </Label>
                <Controller
                  name="defaultGodownId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value?.toString() ?? undefined}
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                      disabled={updateMutation.isPending}
                    >
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Select default warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="null">None</SelectItem>
                        {godownsData.items?.map((godown: Godown) => (
                          <SelectItem key={godown.id} value={godown.id.toString()}>
                            {godown.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="submit" disabled={isLoading || updateMutation.isPending || !isDirty}>
              {updateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Preferences
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 